/**
 * `canary local start` — Start a headed session for a local environment.
 *
 * @module
 */

import process from 'node:process';
import { getArgValue, resolveConfig } from '../auth.js';
import {
  selectProperty,
  promptChoice,
  fetchList,
  downloadStorageState,
} from '../cli-helpers.js';
import { createSession } from '../session/daemon-client.js';
import type { CredentialListItem } from '../cli-helpers.js';

interface LocalEnvironment {
  id: string;
  name: string;
  url: string;
  propertyId: string;
}

export async function runLocalStart(argv: string[]) {
  const config = await resolveConfig(argv);
  const propertyHint = getArgValue(argv, '--application') ?? getArgValue(argv, '--property');
  const envHint = getArgValue(argv, '--environment');
  const credentialHint = getArgValue(argv, '--credential');

  // Select property
  const property = await selectProperty(config.apiUrl, config.token, propertyHint);
  if (!property) {
    process.exitCode = 1;
    return;
  }

  // Get local environments for this property
  const envs = await fetchList<LocalEnvironment>(
    config.apiUrl,
    config.token,
    `/org/properties/${property.id}/environments/local`,
    'items'
  );

  if (envs.length === 0) {
    console.error('No local environments found. Run `canary local init` first.');
    process.exitCode = 1;
    return;
  }

  let selectedEnv: LocalEnvironment;
  if (envHint) {
    const match = envs.find(
      (e) => e.id === envHint || e.name.toLowerCase() === envHint.toLowerCase()
    );
    if (!match) {
      console.error(`Environment "${envHint}" not found.`);
      console.error('Available environments:');
      for (const e of envs) {
        console.error(`  - ${e.name} (${e.url})`);
      }
      process.exitCode = 1;
      return;
    }
    selectedEnv = match;
  } else if (envs.length === 1) {
    selectedEnv = envs[0];
  } else {
    const picked = await promptChoice(
      'Select a local environment:',
      envs.map((e) => ({ label: `${e.name} — ${e.url}`, value: e }))
    );
    if (!picked) {
      process.exitCode = 1;
      return;
    }
    selectedEnv = picked;
  }

  // Look up credentials for this environment
  const allCredentials = await fetchList<CredentialListItem>(
    config.apiUrl,
    config.token,
    '/org/credentials',
    'items'
  );

  const envCredentials = allCredentials.filter(
    (c) => c.propertyId === property.id
  );

  let storageStatePath: string | undefined;
  let credentialName: string | undefined;

  if (envCredentials.length > 0) {
    // Pick a credential if multiple
    let credential: CredentialListItem;
    if (credentialHint) {
      const match = envCredentials.find(
        (c) => c.id === credentialHint || c.name.toLowerCase() === credentialHint.toLowerCase()
      );
      if (!match) {
        console.error(`Credential "${credentialHint}" not found.`);
        console.error('Available credentials:');
        for (const c of envCredentials) {
          console.error(`  - ${c.name}`);
        }
        process.exitCode = 1;
        return;
      }
      credential = match;
    } else if (envCredentials.length === 1) {
      credential = envCredentials[0];
    } else {
      const picked = await promptChoice(
        'Select a credential:',
        envCredentials.map((c) => ({ label: c.name, value: c }))
      );
      if (!picked) {
        process.exitCode = 1;
        return;
      }
      credential = picked;
    }

    credentialName = credential.name;

    // Download storage state if available
    if (credential.storageStateS3Key) {
      console.log('Loading saved login state...');
      storageStatePath = await downloadStorageState({
        apiUrl: config.apiUrl,
        token: config.token,
        propertyId: property.id,
        credentialId: credential.id,
        prefix: 'local',
      });
    }
  }

  // Create headed session
  console.log(`Starting session at ${selectedEnv.url}...`);
  const result = await createSession({
    name: `local-${property.name}`,
    url: selectedEnv.url,
    storageStatePath,
    credentialName,
  });

  if (!result.ok || !result.data) {
    console.error(`Failed to start session: ${result.error ?? 'Unknown error'}`);
    process.exitCode = 1;
    return;
  }

  console.log(`\nSession started: ${result.data.id}`);
  console.log(`  URL: ${selectedEnv.url}`);
  if (credentialName) console.log(`  Credential: ${credentialName}`);
  if (storageStatePath) console.log(`  Login state: loaded`);
  console.log(`\nUse \`canary session\` commands to interact with the browser.`);
  if (credentialName) {
    console.log(
      `If the login expires, run \`canary local login\` — the agent can log you back in without opening a browser.`
    );
  }
}
